import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog, Grid } from '@material-ui/core';
import clsx from 'clsx';
import { useMutation } from '@apollo/client';

import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import { NotificationCard } from 'core/components/notification-card';
import triangleAlertIcon from 'core/assets/svg/triangle-alert.svg';
import { REQUEST_TO_RESUBMIT_POLICY_CHANGES_FOR_APPROVAL } from '../policy.queries.js';
import useSession from 'core/hooks/use-session.jsx';
import { track } from 'core/helpers/analytics.js';

import useStyles from './policy-preview-expired-modal.styles.js';

const PolicyPreviewExpiredModal = ({ open, onClose }) => {
  const [requestToResubmitPolicyChangesForApproval, { loading: requestToResubmitPolicyChangesForApprovalLoading }] =
    useMutation(REQUEST_TO_RESUBMIT_POLICY_CHANGES_FOR_APPROVAL);
  const classes = useStyles();
  const toast = useToast();
  const {
    account: {
      policies: {
        policy: {
          policy: {
            id,
            offer: {
              quote: { rep }
            }
          }
        }
      }
    }
  } = useStore();
  const { isAgency, user } = useSession();

  const handleRequestToResubmitPolicyChangesForApproval = async () => {
    const res = await requestToResubmitPolicyChangesForApproval({
      variables: {
        policyLink: `${window.location.origin}/customer/${id.split('-')[0]}/policy/${id}`,
        agentUsername: rep
      }
    });

    if (res.data.sendResubmitChangesForApprovalCommunication.success) {
      toast.notify({
        type: 'success',
        message: `An email to resubmit the policy change request has been sent to ${res.data.sendResubmitChangesForApprovalCommunication.agentEmail}.`
      });

      track('Change request - Invalid request', {
        isAgency,
        username: user.username
      });

      onClose();
    } else {
      toast.notify({
        type: 'error',
        message: 'Failed to request to resubmit changes for approval. Please contact support.'
      });
    }
  };

  return (
    <Dialog open={open} classes={{ paper: classes.coveragesInfo }}>
      <img src={triangleAlertIcon} className={classes.icon} alt="Paper with two arrows forming a circle" />
      <Label type="h1">This requested change is no longer valid.</Label>
      <div className={classes.content}>
        <Label className={classes.contentLabel} type="body1">
          The policy has changed between the time the change was requested and now.
        </Label>
      </div>
      <NotificationCard type="secondary" className={classes.resubmitChangeRequestPromptCard} hideIcon>
        <div>
          <strong>Do you want the agent to resubmit the change request?</strong>
          <p>This will send an email to the agent</p>
        </div>
        <Grid container className={classes.buttonContainer}>
          <Grid item xs={6} className={classes.buttonGrid}>
            <Button
              variant="contained"
              label="No, don't ask to resubmit"
              mode="light"
              color="secondary"
              className={clsx(classes.button, classes.alignSelfEnd)}
              onClick={onClose}
              disabled={requestToResubmitPolicyChangesForApprovalLoading}
            >
              No, don't ask to resubmit
            </Button>
          </Grid>
          <Grid item xs={6} className={classes.buttonGrid}>
            <Button
              variant="contained"
              label="No, don't ask to resubmit"
              mode="light"
              color="primary"
              className={clsx(classes.button, classes.alignSelfStart)}
              onClick={handleRequestToResubmitPolicyChangesForApproval}
              disabled={requestToResubmitPolicyChangesForApprovalLoading}
            >
              Yes, ask to resubmit
            </Button>
          </Grid>
        </Grid>
      </NotificationCard>
    </Dialog>
  );
};

PolicyPreviewExpiredModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default observer(PolicyPreviewExpiredModal);
